'use client'

import { CreditPackOffer } from '@/components/billing/credit-pack-offer'
import { useProductBrand } from '@/providers/product-brand-provider'

import { CONNECT_BRAND } from '@/lib/connect/brand'

import NumberFlow from '@number-flow/react'
import { CheckIcon, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import type { BillingInterval, PlanSummary } from '@/lib/billing-types'
import { approxChats } from '@/lib/chat-cost-estimate'
import type { PricingDialogVariant } from '@/lib/pricing-dialog-variant'
import { cn } from '@/lib/utils'

const PLAN_DESCRIPTIONS: Record<string, string> = {
  creator: 'For one person shipping creative on a budget.',
  starter: 'For individual creators and solo operators.',
  pro: 'For teams with frequent AI workflow execution.',
  enterprise: 'For organizations requiring scale, security, and control.',
}

// ── Tools-page variant ───────────────────────────────────────────────
// Someone who hits the wall on /tools/* came to generate an image or a
// video, not to buy "automations". Every line here answers that intent: what
// the plan lets them make, how much of it, and that the file they get is the
// full-quality one. Seat/brand limits stay, but after the generation lines.
const TOOLS_PLAN_DESCRIPTIONS: Record<string, string> = {
  creator: 'For one person generating ad images and videos.',
  starter: 'For a solo marketer running several brands.',
  pro: 'For teams generating creative at volume.',
  enterprise: 'For organizations needing scale, security, and control.',
}

const TOOLS_PLAN_FEATURES: Record<string, string[]> = {
  creator: [
    'Full-quality downloads, no watermark',
    'Every image & video model on Soku',
    'Publish creatives straight to ad accounts',
    '1 brand · 1 member',
  ],
  starter: [
    'Everything in Creator',
    'ChatGPT Ads campaigns',
    '5 brands · 5 members',
    'Up to 10 ad accounts per channel',
    'Upload custom skills',
  ],
  pro: [
    'Everything in Starter',
    'Unlimited brands & members',
    'Unlimited ad accounts per channel',
    'Priority support',
  ],
  enterprise: [
    'Everything in Pro',
    'Custom credit volume',
    'SSO + security review',
    'Dedicated success manager',
  ],
}

// House rates from apps/api/routers/landing/fal_tools.py at ~1,078 credits
// per provider dollar: one GPT Image render ≈ 228 credits; a 5-second clip is
// ≈ 324 credits on the H3 tier and ≈ 834 on Seedance Mini 720p. The range is
// what makes the number honest — a plan's video count depends on the model.
const CREDITS_PER_IMAGE = 228
const CREDITS_PER_VIDEO_LOW = 834
const CREDITS_PER_VIDEO_HIGH = 324

// The out-of-credits wall prices a plan in the customer's own chats: credits a
// month over their last-30-day spend per chat (model plus tool spend, including
// renders that booked no chat; the mean, not the median — see `credit_usage.py`).
function chatEstimate(creditsPerMonth: number, creditsPerChat: number): string {
  const chats = approxChats(creditsPerMonth / creditsPerChat)
  if (chats === 0) return 'Less than 1 chat like your recent ones / month'
  return `≈ ${chats.toLocaleString()} ${chats === 1 ? 'chat' : 'chats'} like your recent ones / month`
}

function generationEstimate(creditsPerMonth: number): string {
  const images = Math.floor(creditsPerMonth / CREDITS_PER_IMAGE)
  const videosLow = Math.floor(creditsPerMonth / CREDITS_PER_VIDEO_LOW)
  const videosHigh = Math.floor(creditsPerMonth / CREDITS_PER_VIDEO_HIGH)
  return `≈ ${images.toLocaleString()} images or ${videosLow.toLocaleString()}–${videosHigh.toLocaleString()} short videos / month`
}

const PLAN_FEATURES: Record<string, string[]> = {
  creator: [
    'AI video generation',
    '1 brand and 1 member per organization',
    'Up to 5 ad accounts per channel',
    'Unlimited automations',
    'Integrate with 40+ apps',
    '30+ prebuilt skills',
    'Community support',
  ],
  starter: [
    'Up to 10 organizations',
    'ChatGPT Ads campaigns',
    'Up to 5 brands and 5 members per organization',
    'Up to 10 ad accounts per channel',
    'AI video generation',
    'Unlimited automations',
    'Integrate with 40+ apps',
    '30+ prebuilt skills',
    'Upload custom skills',
  ],
  pro: [
    'Up to 10 organizations',
    'ChatGPT Ads campaigns',
    'Unlimited brands and members',
    'Unlimited ad accounts per channel',
    'AI video generation',
    'Unlimited automations',
    'Integrate with 40+ apps',
    '30+ prebuilt skills',
    'Upload custom skills',
    'Priority support',
  ],
  enterprise: [
    'Up to 10 organizations',
    'ChatGPT Ads campaigns',
    'Unlimited brands and members',
    'Unlimited ad accounts per channel',
    'AI video generation',
    'Unlimited automations',
    'Integrate with 40+ apps',
    '30+ prebuilt skills',
    'Upload custom skills',
    'SSO + security review',
    'Dedicated success manager',
  ],
}

function microsToPrice(micros: number): number {
  return Math.round(micros / 1_000_000)
}

function yearlyTotal(plan: PlanSummary): number {
  return microsToPrice(plan.price_yearly_micros)
}

function yearlyPerMonth(plan: PlanSummary): number {
  return Math.round(yearlyTotal(plan) / 12)
}

export function PricingPlanGrid({
  plans,
  billingCycle,
  variant,
  selectedPlanId = null,
  disabled = false,
  chargesFullTermToday = false,
  creditsPerChat = null,
  onPlanSelect,
}: {
  plans: PlanSummary[]
  billingCycle: BillingInterval
  variant: PricingDialogVariant
  selectedPlanId?: string | null
  disabled?: boolean
  /**
   * Whether picking a plan here starts a Stripe Checkout for the full term.
   *
   * False for anyone who already has a live subscription: they go through
   * `usePlanChangePreview` instead, where the amount due is prorated and a
   * scheduled downgrade charges nothing today. The dialog shows them that real
   * figure in its own confirmation step, so quoting the full annual price on
   * the card would not just be wrong — it would contradict the number on the
   * very next screen. Also false while the subscription is still loading, so
   * the label never appears and then retracts.
   */
  chargesFullTermToday?: boolean
  /** This org's mean credits per chat; when set, each card says how many chats it buys. */
  creditsPerChat?: number | null
  onPlanSelect: (plan: PlanSummary) => void
}) {
  const t = useTranslations('Billing')
  const { product } = useProductBrand()
  if (product === 'toolfare') return <CreditPackOffer disabled={disabled} />

  return (
    <div
      className={cn(
        'grid grid-cols-1 items-stretch gap-5 px-6 pb-7 md:px-9 md:pb-9',
        plans.length > 1 && 'md:grid-cols-2 lg:grid-cols-4',
      )}
    >
      {plans.map(plan => {
        const isEnterprise = plan.credits_per_month === -1
        const priceLabel = isEnterprise
          ? t('contactUs')
          : billingCycle === 'monthly'
            ? microsToPrice(plan.price_monthly_micros)
            : yearlyPerMonth(plan)
        const yearlySavings = isEnterprise
          ? null
          : microsToPrice(plan.price_monthly_micros) * 12 - yearlyTotal(plan)
        const billingSubLabel = isEnterprise
          ? t('flexibleContract')
          : billingCycle === 'monthly'
            ? 'Billed monthly'
            : `Billed $${yearlyTotal(plan)}/year`
        const creditsLabel = isEnterprise
          ? t('unlimitedCredits')
          : billingCycle === 'yearly'
            ? `${(plan.credits_per_month * 12).toLocaleString()} credits / year`
            : `${plan.credits_per_month.toLocaleString()} credits / month`
        const isPro =
          variant === 'connect' ? plan.slug === 'creator' : plan.slug === 'pro'
        const isLoading = selectedPlanId === plan.id
        const isToolsVariant = variant === 'tools'
        const features =
          variant === 'connect' && plan.slug === 'creator'
            ? [
                'Built-in integrations, ready to use',
                'Works from your connected AI app',
                'Credits quoted before every call',
                'Top up credits when you need more',
              ]
            : isToolsVariant
              ? (TOOLS_PLAN_FEATURES[plan.slug] ?? [])
              : (PLAN_FEATURES[plan.slug] ?? [])
        const description =
          variant === 'connect' && plan.slug === 'creator'
            ? `A simple starting point for ${CONNECT_BRAND.name}.`
            : isToolsVariant
              ? (TOOLS_PLAN_DESCRIPTIONS[plan.slug] ?? '')
              : (PLAN_DESCRIPTIONS[plan.slug] ?? '')
        const estimateLabel = isEnterprise
          ? null
          : isToolsVariant
            ? generationEstimate(plan.credits_per_month)
            : creditsPerChat
              ? chatEstimate(plan.credits_per_month, creditsPerChat)
              : null
        // What Stripe will actually ask for on the next screen.
        //
        // Yearly only: the big number on the card is the PER-MONTH figure
        // ($15) and the amount charged is twelve times it ($180). All 19
        // ad-sourced sessions from 08-24..09-21 were checked one by one in
        // Stripe and every one expired unpaid with no card decline behind it —
        // people read the price, reached the payment page, and left. Nine were
        // looking at $180 and two at $2,388. "Billed $180/year" above states
        // the term; this states the transaction, next to the button that starts
        // it, so the Stripe page is never the first place it appears.
        //
        // Monthly needs no such line: the headline price and the charge are the
        // same number, and repeating it would only add noise.
        const chargedTodayLabel =
          chargesFullTermToday && billingCycle === 'yearly' && !isEnterprise
            ? t('chargedToday', {
                amount: `$${yearlyTotal(plan).toLocaleString()}`,
              })
            : null

        return (
          <article
            key={plan.id}
            className={cn(
              'relative flex flex-col rounded-2xl p-6 transition-shadow duration-200 hover:shadow-lg',
              variant === 'connect'
                ? 'min-h-0'
                : isToolsVariant
                  ? 'min-h-[400px]'
                  : 'min-h-[420px]',
              isPro
                ? 'animate-border-spin text-background'
                : 'border-border bg-card border shadow-sm',
            )}
            style={
              isPro
                ? {
                    background:
                      'linear-gradient(var(--foreground), var(--foreground)) padding-box, conic-gradient(from var(--border-angle), transparent 55%, #fb923c 72%, #f97316 85%, #fb923c 92%, transparent) border-box',
                    border: '3px solid transparent',
                  }
                : undefined
            }
          >
            {isPro && (
              <span className="absolute -top-2.5 start-5 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                {t('recommended')}
              </span>
            )}

            <div className={cn('mb-5', isToolsVariant && 'min-h-[176px]')}>
              <p
                className={cn(
                  'text-sm font-medium',
                  isPro ? 'text-background' : 'text-foreground',
                )}
              >
                {plan.name}
              </p>
              {typeof priceLabel === 'number' ? (
                <p
                  className={cn(
                    'mt-2 text-4xl font-semibold tracking-tight',
                    isPro && 'text-background',
                  )}
                >
                  $
                  {variant === 'connect' ? (
                    <span data-connect-plan-price>{priceLabel.toLocaleString()}</span>
                  ) : (
                    <NumberFlow value={priceLabel} />
                  )}
                  <span
                    className={cn(
                      'ms-1 text-lg font-normal',
                      isPro ? 'text-background/60' : 'text-muted-foreground',
                    )}
                  >
                    {t('perMonth')}
                  </span>
                </p>
              ) : (
                <p
                  className={cn(
                    'mt-2 text-4xl font-semibold tracking-tight',
                    isPro && 'text-background',
                  )}
                >
                  {priceLabel}
                </p>
              )}
              <p
                className={cn(
                  'mt-1.5 text-xs',
                  isPro ? 'text-background/60' : 'text-muted-foreground',
                )}
              >
                {billingSubLabel}
                {billingCycle === 'yearly' && yearlySavings != null && (
                  <span
                    className={cn(
                      'ms-1 font-medium',
                      isPro ? 'text-emerald-300' : 'text-emerald-600 dark:text-emerald-400',
                    )}
                  >
                    · Save ${yearlySavings}/yr
                  </span>
                )}
              </p>
              <p
                className={cn(
                  'mt-2.5 text-sm font-medium',
                  isPro ? 'text-emerald-300' : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {creditsLabel}
              </p>
              {estimateLabel && (
                <p
                  className={cn(
                    'mt-1 text-xs',
                    isPro ? 'text-background/70' : 'text-muted-foreground',
                  )}
                >
                  {estimateLabel}
                </p>
              )}
              <p
                className={cn(
                  'mt-2 text-xs leading-relaxed',
                  isPro ? 'text-background/60' : 'text-muted-foreground',
                )}
              >
                {description}
              </p>
            </div>

            <ul
              className={cn(
                'mb-6 space-y-2.5 text-sm',
                isPro ? 'text-background/80' : 'text-muted-foreground',
              )}
            >
              {features.map(feature => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckIcon className="mt-0.5 size-4 shrink-0 text-orange-500" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              variant={isPro ? 'default' : 'outline'}
              className={cn(
                'mt-auto w-full',
                isPro && 'bg-orange-500 text-white hover:bg-orange-600',
              )}
              onClick={() => onPlanSelect(plan)}
              disabled={disabled}
            >
              {isLoading && <Loader2 className="me-2 size-4 animate-spin" />}
              {plan.slug === 'enterprise'
                ? t('talkToFounders')
                : t('startPlan', { plan: plan.name })}
            </Button>
            {chargedTodayLabel && (
              <p
                className={cn(
                  'mt-2 text-center text-xs',
                  isPro ? 'text-background/70' : 'text-muted-foreground',
                )}
              >
                {chargedTodayLabel}
              </p>
            )}
          </article>
        )
      })}
    </div>
  )
}
